import React, { useState, useEffect } from "react";
import {
  Modal,
  TextField,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  createAbbreviations,
  createAlias,
  createAnglicized,
  createBadData,
  createSingleDouble,
  createInitials,
  createNickNames,
  createIntervening,
  createCountry,
} from "../../../Api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const CreateModal = ({
  open,
  onClose,
  selectedTab,
  handleCreateSubmit,
  createFormData,
  handleCreateFormChange,
  resetCreateForm,
}) => {
  const [selectedAliasType, setSelectedAliasType] = useState("");
  const [formValues, setFormValues] = useState({
    correl_id: localStorage.getItem("correl_id") || "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Reset form when modal opens with only correl_id
    if (open) {
      setFormValues({
        correl_id: localStorage.getItem("correl_id") || "",
      });
    }
  }, [open]);

  // Create a function to handle form submission - Api
  const handleUpdate = async () => {
    try {
      // Choose source of truth: parent createFormData (if handler provided) else local formValues
      const sourceValues =
        handleCreateFormChange && createFormData
          ? {
              ...createFormData,
              correl_id:
                createFormData.correl_id ||
                localStorage.getItem("correl_id") ||
                "",
            }
          : {
              ...formValues,
              correl_id:
                formValues.correl_id || localStorage.getItem("correl_id") || "",
            };

      // Validate required fields based on tab
      const fields = getFields();
      for (const field of fields) {
        if (!sourceValues[field.name]?.trim()) {
          Swal.fire({
            text: `Please enter ${field.label.toLowerCase()}`,
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      }
      if (selectedTab == 0) {
        const name = sourceValues.name?.trim() || "";
        const abbr = sourceValues.abbreviations?.trim() || "";

        const nameHasLetter = /[a-zA-Z]/.test(name);
        const abbrHasLetter = /[a-zA-Z]/.test(abbr);

        //  Invalid if name or abbreviation has NO letters at all
        if (!nameHasLetter || !abbrHasLetter) {
          Swal.fire({
            text: "Numbers/Special characters/Combination of Numbers and Special Characters alone are not allowed.",
            icon: "warning",
            showConfirmButton: false,
            timer: 4000,
          });
          return;
        }
        if (
          sourceValues.name?.trim().toLowerCase() ===
          sourceValues.abbreviations?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Abbreviation and Replace Word cannot be same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab == 1) {
        if (
          sourceValues.name?.trim().toLowerCase() ===
          sourceValues.alias_word?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Name and Alias Name cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        } else if (selectedAliasType === "") {
          Swal.fire({
            text: "Alias Type is required",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
        // else if (sourceValues.name?.trim().length < 2) {
        //   Swal.fire({
        //     text: "The Name must contain a minimum of 2 characters.",
        //     icon: "warning",
        //     showConfirmButton: false,
        //     timer: 3000,
        //   });
        //   return;
        // }
      } else if (selectedTab === 2) {
        // Anglicized
        if (
          sourceValues.entity_name?.trim().toLowerCase() ===
          sourceValues.anglicized_word?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Name and Anglicized Name cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 4) {
        // Double letters
        if (
          sourceValues.letter?.trim().toLowerCase() ===
          sourceValues.replace_letter?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Letter Code and Replace Letter Code cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        } else if (
          sourceValues.letter?.trim().length ===
          sourceValues.replace_letter?.trim().length
        ) {
          Swal.fire({
            text: "If Letter Code is single letter then Replace Letter Code should be double letter or vice versa",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
        const letterLower = sourceValues.letter?.trim();
        const replaceLower = sourceValues.replace_letter?.trim();

        const letterLen = letterLower.length;
        const replaceLen = replaceLower.length;
        // 3) Validate pattern like h <-> hh, a <-> aa, etc.
        // single → double OR double → single (vice versa)
        if (letterLen === 1 && replaceLen === 2) {
          // Expect replace = letter repeated twice
          if (replaceLower !== letterLower.repeat(2)) {
            Swal.fire({
              text: "Replace Letter Code must be the double of Letter Code (e.g. A → AA) and Case Sensitive. ",
              icon: "warning",
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }
        } else if (letterLen === 2 && replaceLen === 1) {
          // Expect letter = replace repeated twice
          if (letterLower !== replaceLower.repeat(2)) {
            Swal.fire({
              text: "Letter Code must be the double of Replace Letter Code (e.g. AA → A) and Case Sensitive.",
              icon: "warning",
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }
        }
      } else if (selectedTab === 5) {
        // Initials
        if (
          sourceValues.name?.trim().toLowerCase() ===
          sourceValues.initial?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Name and Initials cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        } else if (
          sourceValues.name?.trim()?.length <
          sourceValues.initial?.trim()?.length
        ) {
          Swal.fire({
            text: "Length of the Name should always be greater than Initial",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
        //  else if (/[a-z]/.test(sourceValues.initial?.trim())) {
        //   Swal.fire({
        //     text: "Lowercase letters are not allowed in Initials",
        //     icon: "warning",
        //     showConfirmButton: false,
        //     timer: 3000,
        //   });
        //   return;
        // }
        else if (
          sourceValues.name?.trim()?.length <
          sourceValues.initial?.trim()?.length
        ) {
          Swal.fire({
            text: "Length of the Name should always be greater than Initial",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        } else if (
          sourceValues.name?.trim() &&
          !sourceValues.name.trim().includes(" ") &&
          sourceValues.initial?.trim()?.includes(",")
        ) {
          Swal.fire({
            text: "For a single name, multiple initials separated by commas are not allowed",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 6) {
        // Nicknames
        const nickname = sourceValues.nick_name?.trim();
        const name = sourceValues.name?.trim();
        if (name?.length < 2) {
          Swal.fire({
            text: "The Name must contain a minimum of 2 characters.",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
        //  else if (nickname?.length === 1 || name?.length === 1) {
        //     Swal.fire({
        //       text: "Single-letter names or nicknames are not allowed",
        //       icon: "warning",
        //       showConfirmButton: false,
        //       timer: 3000,
        //     });
        //     return;
        //   }
        else if (name?.trim().toLowerCase() === nickname?.toLowerCase()) {
          Swal.fire({
            text: "Name and Nickname cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 7) {
        const intervening = sourceValues.word_list?.trim() || "";

        if (intervening.length < 2) {
          Swal.fire({
            text: "The Intervening Name must contain a minimum of 2 characters.",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 8) {
        const country = sourceValues.country?.trim() || "";

        // Check if the first character is NOT an alphabet
        if (!/^[A-Za-z]/.test(country)) {
          Swal.fire({
            text: "Country name must start with an alphabet.",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      }

      let response;
      let payload = { correl_id: sourceValues.correl_id };

      // Construct minimal payload based on tab
      switch (selectedTab) {
        case 0: // Abbreviations
          payload = {
            name: sourceValues.name,
            abbreviations: sourceValues.abbreviations,
            correl_id: sourceValues.correl_id,
          };
          response = await createAbbreviations(payload);
          break;
        case 1: // Aliases
          payload = {
            name: sourceValues.name,
            alias_word: sourceValues.alias_word,
            alias_type: selectedAliasType || "",
            correl_id: sourceValues.correl_id,
          };
          response = await createAlias(payload);
          break;
        case 2: // Anglicized
          payload = {
            entity_name: sourceValues.entity_name,
            anglicized_word: sourceValues.anglicized_word,
            correl_id: sourceValues.correl_id,
          };
          response = await createAnglicized(payload);
          break;
        case 3: // Bad Data
          payload = {
            special_char: sourceValues.special_char,
            // description: sourceValues.description,
            correl_id: sourceValues.correl_id,
          };
          response = await createBadData(payload);
          break;
        case 4: // Double letters
          payload = {
            letter: sourceValues.letter,
            replace_letter: sourceValues.replace_letter,
            correl_id: sourceValues.correl_id,
          };
          response = await createSingleDouble(payload);
          break;
        case 5: // Initials
          payload = {
            name: sourceValues.name,
            initial: sourceValues.initial,
            correl_id: sourceValues.correl_id,
          };
          response = await createInitials(payload);
          break;
        case 6: // Nicknames
          payload = {
            name: sourceValues.name,
            nick_name: sourceValues.nick_name,
            correl_id: sourceValues.correl_id,
          };
          response = await createNickNames(payload);
          break;
        case 7: // Intervening
          payload = {
            word_list: sourceValues.word_list,
            description: sourceValues.description,
            correl_id: sourceValues.correl_id,
          };
          response = await createIntervening(payload);
          break;
        case 8: // Countries
          payload = {
            country: sourceValues.country,
            correl_id: sourceValues.correl_id,
          };
          response = await createCountry(payload);
          break;
        default:
          break;
      }

      Swal.close();

      if (response.status === "S" && response.result_code === 200) {
        Swal.fire({
          text: "Record created successfully!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
          heightAuto: false,
        }).then(() => {
          handleCreateSubmit();
          resetCreateForm();
          setSelectedAliasType("");
          onClose();
        });
      } else if (response?.result_code === 409) {
        Swal.fire({
          text:
            response?.message || "Data already exists. Please check your data.",
          icon: "warning",
          showConfirmButton: false,
          timer: 3000,
        });
      } else if (
        response.status === "F" &&
        (response.message === "Unauthorized - Missing token" ||
          response.message === "Unauthorized - Invalid token")
      ) {
        Swal.fire({
          text: "Please login with your credentials",
          icon: "warning",
          showConfirmButton: false,
          timer: 3000,
        }).then(() => {
          localStorage.clear();
          navigate("/login");
        });
      } else {
        Swal.fire({
          text: response.message || "Error creating record",
          icon: "error",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error("Error creating record:", error);
      Swal.fire({
        text: "Error creating record",
        icon: "error",
        showConfirmButton: false,
        timer: 1500,
        heightAuto: false,
      });
    }
  };

  const getModalTitle = () => {
    switch (selectedTab) {
      case 0:
        return "Create Abbreviation";
      case 1:
        return "Create Alias";
      case 2:
        return "Create Anglicized";
      case 3:
        return "Create Bad Data";
      case 4:
        return "Create Double Letter";
      case 5:
        return "Create Initial";
      case 6:
        return "Create Nickname";
      case 7:
        return "Create Intervening";
      case 8:
        return "Create Country";
      default:
        return "Create Record";
    }
  };

  const getFields = () => {
    switch (selectedTab) {
      case 0: // Abbreviations
        return [
          { name: "name", label: "Abbreviation", isRequired: true },
          { name: "abbreviations", label: "Replace Word", isRequired: true },
        ];
      case 1: // Aliases
        return [
          { name: "name", label: "Name", isRequired: true },
          { name: "alias_word", label: "Alias Name", isRequired: true },
        ];
      case 2: // Anglicized
        return [
          { name: "entity_name", label: "Name", isRequired: true },
          {
            name: "anglicized_word",
            label: "Anglicized Name",
            isRequired: true,
          },
        ];
      case 3: // Bad Data
        return [
          { name: "special_char", label: "Bad_Data", isRequired: true },
          // { name: "description", label: "Bad Data", isRequired: true },
        ];
      case 4: // Double letters
        return [
          { name: "letter", label: "Letter Code", isRequired: true },
          {
            name: "replace_letter",
            label: "Replace Letter Code",
            isRequired: true,
          },
        ];
      case 5: // Initials
        return [
          { name: "name", label: "Name", isRequired: true },
          { name: "initial", label: "Initials", isRequired: true },
        ];
      case 6: // Nicknames
        return [
          { name: "name", label: "Name", isRequired: true },
          { name: "nick_name", label: "Nickname", isRequired: true },
        ];
      case 7: // Intervening
        return [
          { name: "word_list", label: "Intervening Words", isRequired: true },
          // { name: "description", label: "Intervening", isRequired: true },
        ];
      case 8: // Countries
        return [{ name: "country", label: "Country Name", isRequired: true }];
      default:
        return [];
    }
  };
  return (
    <Modal open={open} onClose={onClose} aria-labelledby="create-modal-title">
      <div className="modal-overlay">
        <div
          className="modal-container bg-white p-4 rounded"
          style={{ width: 400, maxWidth: "90vw" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 id="create-modal-title">{getModalTitle()}</h5>
            <IconButton
              onClick={() => {
                onClose();
                resetCreateForm();
                setSelectedAliasType("");
              }}
              color="black"
              sx={{ fontWeight: 600 }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          {getFields().map((field, index) => (
            <TextField
              key={field.name}
              className={index > 0 ? "w-100 mt-3" : "w-100"}
              label={field.label}
              variant="outlined"
              autoComplete="off"
              required
              value={
                (handleCreateFormChange && createFormData
                  ? createFormData[field.name]
                  : formValues[field.name]) || ""
              }
              onChange={(ev) => {
                // If parent passed a handler, call it so parent state stays in sync;
                // otherwise, fall back to local state as before.
                if (handleCreateFormChange) {
                  // sanitize value in the same way as before then call parent handler
                  let newValue = ev.target.value;
                  if (selectedTab == 2) {
                    newValue = newValue.replace(/[^a-zA-Z]/g, "");
                  } else if (selectedTab == 4) {
                    newValue = newValue.replace(/[^a-zA-Z]/g, "").slice(0, 2);
                  } else if (selectedTab == 5) {
                    //exculuding numbers for
                    newValue = newValue.replace(/[0-9]/g, "");
                  } else if (selectedTab == 6) {
                    // For Nicknames, only alphabets
                    newValue = newValue.replace(/[^a-zA-Z-\s]/g, "");
                  } else if (selectedTab == 7) {
                    newValue = newValue.replace(/[^a-zA-Z\s]/g, "");
                  }
                  // else if (selectedTab == 8) {
                  //   newValue = newValue.replace(/[^a-zA-Z\s]/g, "");
                  // }
                  // else {
                  //   const invalidPattern =
                  //     /<script.*?>.*?<\/script>|[<>\\{}=:/""'']/i;
                  //   if (invalidPattern.test(newValue)) return;
                  // }
                  // emulate event shape expected by parent's handleCreateFormChange
                  handleCreateFormChange({
                    target: { name: field.name, value: newValue },
                  });
                } else {
                  // local fallback (unchanged)
                  if (selectedTab == 4) {
                    const value = ev.target.value
                      .replace(/[^a-zA-Z]/g, "")
                      .slice(0, 2);
                    setFormValues({ ...formValues, [field.name]: value });
                  } else if (selectedTab == 5) {
                    const value = ev.target.value.replace(/[^a-zA-Z]/g, "");
                    setFormValues({ ...formValues, [field.name]: value });
                  } else if (selectedTab == 6) {
                    const value = ev.target.value.replace(/[^a-zA-Z\-]/g, "");
                    setFormValues({ ...formValues, [field.name]: value });
                  } else if (selectedTab == 8) {
                    const value = ev.target.value.replace(/[^a-zA-Z\s]/g, "");
                    setFormValues({ ...formValues, [field.name]: value });
                  }
                  //  else {
                  //   const value = ev.target.value;
                  //   const invalidPattern =
                  //     /<script.*?>.*?<\/script>|[<>\\{}=:/""'']/i;
                  //   if (!invalidPattern.test(value)) {
                  //     setFormValues({ ...formValues, [field.name]: value });
                  //   }
                  // }
                }
              }}
              inputProps={{
                maxLength: 255,
              }}
            />
          ))}
          {selectedTab === 1 && (
            <div style={{ marginBottom: 16 }}>
              <Autocomplete
                className="mt-3"
                options={["Strong", "Weak"]} // ✅ No iterator, static values
                value={selectedAliasType || null}
                onChange={(_, newValue) => {
                  setSelectedAliasType(newValue || "");
                }}
                clearOnEscape
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Alias Type"
                    variant="outlined"
                    style={{ minWidth: 220 }}
                  />
                )}
              />
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="outlined"
              onClick={() => {
                onClose();
                resetCreateForm();
                setSelectedAliasType("");
              }}
              className="me-2"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={() => handleUpdate()}
            >
              Create
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateModal;
